import { Disclosure, Switch } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Dialog, DialogContent } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../api/client';
import { UserProfile } from '../../components/settings/user-profile';
import { Button } from '../../components/ui/button';
import { FaceVerifyScreens } from '../../constants/face.const';
import { PhoneUpdateFlowStep } from '../../constants/settings.const';
import { useApp as portalApp } from '../../context/usePortal';
import { useApp as contextApp } from '../../context/user-context';
import { cn } from '../../lib/utils';
import { Loader } from '../../shared/Loader';
import ClosePopupConfirmationPopup from './components/ClosePopupConfirmationPopup';
import DeleteAccount from './components/DeleteAccount';
import RemovePin from './components/RemovePin';
import ResetPin from './components/ResetPin';
import ResetPINPopup from './components/ResetPINPopup';
import SecuritySettings from './components/SecuritySettings';
import SetPin from './components/SetPin';
import UpdateFaceID from './components/UpdateFaceID';
import UpdatePhoneNumber from './components/UpdatePhoneNumber';
import { personalInfoOf } from '../../utils/security-phrase';

export type SensitiveFieldUpdate = {
  field: 'pin' | 'face_index' | 'user_phone_number' | 'security_phrase';
  changed_at: string;
};

interface LastUpdatesResponse {
  success: boolean;
  status: number;
  data: {
    lastUpdates: SensitiveFieldUpdate[];
  };
}

export default function Settings() {
  const { userInfo } = contextApp();
  const { user, saveUser, refreshUser } = portalApp();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    newsletter: user?.newsletter ?? false,
    text_message: user?.text_message ?? false,
    marketing_and_offers: user?.marketing_and_offers ?? false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFaceVerificationLoading, setIsFaceVerificationLoading] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [dataChanged, setDataChanged] = useState(false);
  const [showResetPINPopup, setShowResetPINPopup] = useState<string>('');

  const [searchParams, setSearchParams] = useSearchParams();
  const sdkToken = searchParams.get('token');

  const onVerifyFaceError = () => {
    const params = new URLSearchParams(window.location.search);
    const flowType = params.get('flow') as FaceVerifyScreens;

    params.set('isVerified', 'false');
    params.set('flow', flowType);
    params.delete('token');
    setSearchParams(params);
  };

  const verifyFace = useMutation({
    mutationFn: () => {
      setIsFaceVerificationLoading(true);
      return apiClient
        .post<{
          success: boolean;
          status: number;
          data: { isVerified: boolean };
        }>('/users/verify/face', {
          sdkToken
        })
        .then((res) => res.data);
    },
    retry: false,
    onSuccess: (response) => {
      setIsFaceVerificationLoading(false);
      if (!response?.data?.isVerified) {
        onVerifyFaceError();
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const flowType = params.get('flow') as FaceVerifyScreens;

      params.set('isVerified', 'true');
      params.set('flow', flowType);
      // For update phone flow, continue to EnterNewPhone or Pin step
      // For reset pin flow, continue to phone verification (which leads to success)
      if (flowType === FaceVerifyScreens.UPDATE_PHONE) {
        params.set('step', PhoneUpdateFlowStep.EnterPin.toString());
      } else if (flowType === FaceVerifyScreens.UPDATE_PHONE_2) {
        params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
      }

      params.delete('token');
      setSearchParams(params);
    },
    onError: (error) => {
      setIsFaceVerificationLoading(false);
      console.error('Face verify error', error);
      onVerifyFaceError();
    }
  });

  const hasCalled = useRef(false);
  useEffect(() => {
    if (sdkToken && !hasCalled.current) {
      verifyFace.mutate();
      hasCalled.current = true;
    }
  }, [sdkToken, verifyFace]);

  const { data: lastUpdatesData } = useQuery<LastUpdatesResponse>({
    queryKey: ['getLastUpdates'],
    queryFn: async () => {
      const resp = await apiClient.get('/users/last-updates');
      return resp.data;
    }
  });

  const lastUpdatedPin =
    lastUpdatesData?.data?.lastUpdates.find(
      (update: SensitiveFieldUpdate) => update.field === 'pin'
    )?.changed_at || '';
  const lastUpdatedFace =
    lastUpdatesData?.data?.lastUpdates.find(
      (update: SensitiveFieldUpdate) => update.field === 'face_index'
    )?.changed_at || '';
  const lastUpdatedPhone =
    lastUpdatesData?.data?.lastUpdates.find(
      (update: SensitiveFieldUpdate) => update.field === 'user_phone_number'
    )?.changed_at || '';

  const lastUpdatedSecurityPhrase =
    lastUpdatesData?.data?.lastUpdates.find(
      (update: SensitiveFieldUpdate) => update.field === 'security_phrase'
    )?.changed_at || '';

  const handleShowResetPINPopupChange = (value: string) => {
    setShowResetPINPopup(value);
  };

  const onCloseResetPINDialog = () => {
    setShowConfirmation(true);
  };

  const handleConfirmCloseResetPIN = () => {
    handleShowResetPINPopupChange('');
    setShowConfirmation(false);
  };

  const handleNotificationChange = (checked: any, event: any) => {
    setDataChanged(true);
    const { name } = event.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: checked
    }));
  };

  useEffect(() => {
    setIsLoading(true);
    const handleSave = async () => {
      const response = await saveUser({
        ...settings,
        user_name: user?.user_name ?? '',
        user_email: user?.user_email ?? '',
        user_birthday: user?.user_birthday ?? ''
      });
      if (response?.isOk()) {
        toast.success('Profile updated successfully.');
      } else toast.error('Some error occured');
    };
    if (dataChanged) handleSave();
    setIsLoading(false);
  }, [settings]);

  // Update face is disabled
  // const params = new URLSearchParams(window.location.search)
  // useEffect(() => {
  //   const face_index = params.get('token')
  //   if (face_index) {
  //     const handleUpdateFace = async () => {
  //       const response = await ayeApi.updateFace({ body: { face_index } })
  //       if (response.success) {
  //         toast.success('Face updated successfully')
  //       } else {
  //         toast.error('Error updating face')
  //       }
  //       navigate('/settings')
  //     }
  //     handleUpdateFace()
  //   }
  // }, [params])

  if (isLoading || isFaceVerificationLoading) {
    return (
      <div
        style={{ minHeight: `calc(100vh - 64px)` }}
        className="flex items-center justify-center"
      >
        <div className="flex flex-col items-center gap-4">
          <Loader />
          {isFaceVerificationLoading && (
            <p className="mt-12 text-gray-600">
              Verifying face authentication...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <Disclosure
          as="div"
          className="relative overflow-hidden bg-orange-600 pb-32"
        >
          {({ open }) => (
            <>
              <div
                aria-hidden="true"
                className={cn(
                  open ? 'bottom-0' : 'inset-y-0',
                  'absolute inset-x-0 left-1/2 w-full -translate-x-1/2 transform overflow-hidden lg:inset-y-0'
                )}
              >
                <div className="absolute inset-0 flex">
                  <div
                    className="h-full w-1/2"
                    style={{ backgroundColor: '#f77f00' }}
                  />
                  <div
                    className="h-full w-1/2"
                    style={{ backgroundColor: '#fd9d01' }}
                  />
                </div>
                <div className="relative flex justify-center">
                  <svg
                    className="flex-shrink-0"
                    width={1750}
                    height={308}
                    viewBox="0 0 1750 308"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M284.161 308H1465.84L875.001 182.413 284.161 308z"
                      fill="#f77f00" // Adjusted to orange shade
                    />
                    <path
                      d="M1465.84 308L16.816 0H1750v308h-284.16z"
                      fill="#fd7e14" // Adjusted to another orange shade
                    />
                    <path
                      d="M1733.19 0L284.161 308H0V0h1733.19z"
                      fill="#fb8c00" // Adjusted to another orange shade
                    />
                    <path
                      d="M875.001 182.413L1733.19 0H16.816l858.185 182.413z"
                      fill="#ff9f00" // Adjusted to another orange shade
                    />
                  </svg>
                </div>
              </div>
              <header className="relative py-10">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                  <h1 className="text-3xl font-bold tracking-tight text-white">
                    Settings
                  </h1>
                </div>
              </header>
            </>
          )}
        </Disclosure>

        <main className="relative -mt-32">
          <div className="mx-auto max-w-screen-xl px-4 pb-6 sm:px-6 lg:px-8 lg:pb-16">
            <div className="overflow-hidden rounded-lg bg-white shadow">
              <div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-x lg:divide-y-0">
                <div className="divide-y divide-gray-200 lg:col-span-12">
                  <UserProfile />

                  {/* Privacy section */}
                  <div className="divide-y divide-gray-200">
                    <div className="px-4 pb-3 pt-2 sm:px-6">
                      <div>
                        <h2 className="text-lg font-medium leading-6 text-gray-900">
                          Security
                        </h2>

                        <div className="flex items-center justify-between gap-2 py-4">
                          <div className="flex flex-col gap-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              Scan & Pay without PIN
                            </h3>
                            <p className="text-sm text-gray-500">
                              Turn it on to opt out PIN requirement for
                              transactions below the set limit
                            </p>
                            {!user?.has_pin && (
                              <p className="text-sm text-orange-500">
                                Set a PIN first to enable this.
                              </p>
                            )}
                          </div>
                          <Button
                            type="button"
                            className="min-w-24"
                            disabled={!user?.has_pin}
                            onClick={() => navigate('/settings/pin-limit')}
                          >
                            Set
                          </Button>
                        </div>

                        {!user?.has_pin ? (
                          <SetPin onSuccess={refreshUser} />
                        ) : (
                          <>
                            <ResetPin lastUpdated={lastUpdatedPin} />
                            <RemovePin onSuccess={refreshUser} />
                          </>
                        )}
                        <SecuritySettings
                          currentPhrase={user?.security_phrase ?? null}
                          hasAvatar={!!user?.has_security_avatar}
                          avatarImage={user?.security_avatar_image ?? null}
                          lastUpdated={lastUpdatedSecurityPhrase}
                          personal={personalInfoOf(user)}
                          onSuccess={refreshUser}
                        />
                        <UpdateFaceID lastUpdated={lastUpdatedFace} />
                        <UpdatePhoneNumber lastUpdated={lastUpdatedPhone} />
                      </div>
                    </div>

                    <div className="px-4 pt-2 sm:px-6">
                      <div>
                        <h2 className="mt-1 text-lg font-medium leading-6 text-gray-900">
                          Manage Your Notifications
                        </h2>
                      </div>
                      <ul className="mt-2 divide-y divide-gray-200">
                        <Switch.Group
                          as="li"
                          className="flex items-center justify-between py-4"
                        >
                          <div className="flex flex-col">
                            <Switch.Label
                              as="p"
                              className="text-sm font-medium text-gray-900"
                              passive
                            >
                              Text Message Alerts
                            </Switch.Label>
                            <Switch.Description className="text-sm text-gray-500">
                              Receive essential, real-time alerts and reminders
                              directly to your phone, keeping you informed on
                              what matters most and protecting your rights.
                            </Switch.Description>
                          </div>
                          <Switch
                            name="text_message"
                            checked={
                              settings?.text_message ?? userInfo?.text_message
                            }
                            onChange={(checked) =>
                              handleNotificationChange(checked, {
                                target: { name: 'text_message' }
                              })
                            }
                            className={cn(
                              settings?.text_message
                                ? 'bg-orange-500'
                                : 'bg-gray-200',
                              'relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none'
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={cn(
                                settings?.text_message
                                  ? 'translate-x-5'
                                  : 'translate-x-0',
                                'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </Switch>
                        </Switch.Group>
                        <Switch.Group
                          as="li"
                          className="flex items-center justify-between py-4"
                        >
                          <div className="flex flex-col">
                            <Switch.Label
                              as="p"
                              className="text-sm font-medium text-gray-900"
                              passive
                            >
                              Newsletter Subscriptions
                            </Switch.Label>
                            <Switch.Description className="text-sm text-gray-500">
                              Get the scoop on the latest trends in tech,
                              security, privacy, payments, and financial
                              literacy. Receive curated content from us that
                              keeps you both informed and engaged.
                            </Switch.Description>
                          </div>
                          <Switch
                            name="newsletter"
                            checked={
                              settings?.newsletter ?? userInfo?.newsletter
                            }
                            onChange={(checked) =>
                              handleNotificationChange(checked, {
                                target: { name: 'newsletter' }
                              })
                            }
                            className={cn(
                              settings?.newsletter
                                ? 'bg-orange-500'
                                : 'bg-gray-200',
                              'relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none'
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={cn(
                                settings?.newsletter
                                  ? 'translate-x-5'
                                  : 'translate-x-0',
                                'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </Switch>
                        </Switch.Group>
                        <Switch.Group
                          as="li"
                          className="flex items-center justify-between py-4"
                        >
                          <div className="flex flex-col">
                            <Switch.Label
                              as="p"
                              className="text-sm font-medium text-gray-900"
                              passive
                            >
                              Marketing & Offers
                            </Switch.Label>
                            <Switch.Description className="text-sm text-gray-500">
                              Be the first to know about new features, offers,
                              and opportunities so you can maximize your
                              potential and reap the rewards.
                            </Switch.Description>
                          </div>
                          <Switch
                            name="marketing_and_offers"
                            checked={
                              settings?.marketing_and_offers ??
                              userInfo?.marketing_and_offers
                            }
                            onChange={(checked) =>
                              handleNotificationChange(checked, {
                                target: { name: 'marketing_and_offers' }
                              })
                            }
                            className={cn(
                              settings?.marketing_and_offers
                                ? 'bg-orange-500'
                                : 'bg-gray-200',
                              'relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none'
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={cn(
                                settings?.marketing_and_offers
                                  ? 'translate-x-5'
                                  : 'translate-x-0',
                                'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </Switch>
                        </Switch.Group>
                      </ul>
                    </div>

                    <DeleteAccount />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showResetPINPopup.toLowerCase().trim() !== '' && (
            <div className="rounded-2xl p-0">
              <Dialog
                open={showResetPINPopup.toLowerCase().trim() !== ''}
                onClose={onCloseResetPINDialog}
                className="boder-red-300 rounded-xl border-1 p-0"
              >
                <DialogContent
                  dividers
                  className="min-w-[200px] max-w-[420px] p-0"
                >
                  <button
                    onClick={onCloseResetPINDialog}
                    className="ms-auto block"
                  >
                    <XMarkIcon className="h-6 text-end" />
                  </button>
                  <ResetPINPopup
                    onClose={handleConfirmCloseResetPIN}
                    handleShowResetPINPopupChange={
                      handleShowResetPINPopupChange
                    }
                    showResetPINPopup={showResetPINPopup}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}

          <ClosePopupConfirmationPopup
            open={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handleConfirmCloseResetPIN}
            message="Quit Resetting PIN?"
          />
        </main>
      </div>
    </>
  );
}
